import { API_ENDPOINT } from '../constant/constant.ts'
import { getCookie, setCookie } from '@/util/cookie.ts'
import { KEY_ACCESS_TOKEN } from '@/store/LoginStore.ts'

// 토큰 갱신 중인지 확인하는 플래그
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
}> = []

const processQueue = (error: any = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve()
        }
    })
    failedQueue = []
}

export interface IApiGetProps {
    cookies?: {}
}

export interface ApiRes<T> {
    data: T
    code?: string | number
    message?: string
}

export const apiDownload = async (url: string): Promise<Blob> => {
    let endpoint = `${API_ENDPOINT}${url}`

    try {
        let response = await fetch(endpoint, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        })

        if (response.status === 401) {
             // Simple retry logic or just throw for now as implementing full refresh here is redundant code
             // Ideally we refactor to baseRequest like carearly
             throw new Error('Unauthorized')
        }

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        return await response.blob()
    } catch (error) {
        throw error
    }
}

export const apiGet = async <T>(url: string, isMocking: boolean = false): Promise<ApiRes<T>> => {
    let endpoint = `${API_ENDPOINT}${url}`
    if (isMocking) {
        endpoint = url
    }

    try {
        let response = await fetch(endpoint, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include' // HttpOnly Cookie 전송을 위해 필요
        })

        // 401 에러 처리
        if (response.status === 401 && !url.includes('/auth/')) {
            if (isRefreshing) {
                // 이미 갱신 중이면 대기
                await new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                // 갱신 완료 후 재시도
                response = await fetch(endpoint, {
                    method: 'GET',
                    headers: getHeaders(),
                    credentials: 'include'
                })
            } else {
                // 토큰 갱신 시도
                isRefreshing = true
                try {
                    const refreshResponse = await fetch(`${API_ENDPOINT}/auth/refresh`, {
                        method: 'POST',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include'
                    })

                    if (!refreshResponse.ok) {
                        throw new Error('Refresh failed')
                    }

                    const refreshData = await refreshResponse.json()
                    const newAccessToken = refreshData.data?.accessToken || refreshData.data?.token
                    
                    if (newAccessToken) {
                        setCookie(KEY_ACCESS_TOKEN, newAccessToken, { 
                            path: '/', 
                            maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                        })
                        
                        // Zustand State도 업데이트
                        const { useLoginStore } = await import('@/store/LoginStore.ts')
                        useLoginStore.getState().login('', newAccessToken)
                        
                        processQueue(null)
                        
                        // 새 토큰으로 재시도
                        response = await fetch(endpoint, {
                            method: 'GET',
                            headers: getHeaders(),
                            credentials: 'include'
                        })
                    } else {
                        throw new Error('No access token in refresh response')
                    }
                } catch (error) {
                    processQueue(error)
                    // 갱신 실패 시 로그아웃
                    const { useLoginStore } = await import('@/store/LoginStore.ts')
                    useLoginStore.getState().logout()
                    window.location.href = '/login'
                    throw error
                } finally {
                    isRefreshing = false
                }
            }
        }

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
        }

        const res = await response.json()
        
        if (Array.isArray(res)) {
            return { data: res as T, code: 200, message: 'success' }
        } else if (res.data !== undefined) {
            return { data: res.data, code: res.code, message: res.message }
        } else {
            return { data: res as T, code: 200, message: 'success' }
        }
    } catch (error) {
        throw error
    }
}

export const apiPost = async <T>(url: string, body?: any, isMocking: boolean = false): Promise<ApiRes<T>> => {
    let endpoint = `${API_ENDPOINT}${url}`
    if (isMocking) {
        endpoint = url
    }

    let response = await fetch(endpoint, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body),
        credentials: 'include' // HttpOnly Cookie 전송을 위해 필요
    })

    // 401 에러 처리 (login, refresh는 제외)
    if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        if (isRefreshing) {
            await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
            response = await fetch(endpoint, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(body),
                credentials: 'include'
            })
        } else {
            isRefreshing = true
            try {
                const refreshResponse = await fetch(`${API_ENDPOINT}/auth/refresh`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                })

                if (!refreshResponse.ok) {
                    throw new Error('Refresh failed')
                }

                const refreshData = await refreshResponse.json()
                const newAccessToken = refreshData.data?.accessToken || refreshData.data?.token
                
                if (newAccessToken) {
                    setCookie(KEY_ACCESS_TOKEN, newAccessToken, { 
                        path: '/', 
                        maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                    })
                    
                    // Zustand State도 업데이트
                    const { useLoginStore } = await import('@/store/LoginStore.ts')
                    useLoginStore.getState().login('', newAccessToken)
                    
                    processQueue(null)
                    
                    response = await fetch(endpoint, {
                        method: 'POST',
                        headers: getHeaders(),
                        body: JSON.stringify(body),
                        credentials: 'include'
                    })
                } else {
                    throw new Error('No access token in refresh response')
                }
            } catch (error) {
                processQueue(error)
                const { useLoginStore } = await import('@/store/LoginStore.ts')
                useLoginStore.getState().logout()
                window.location.href = '/login'
                throw error
            } finally {
                isRefreshing = false
            }
        }
    }

    const res = await response.json()
    return { data: res.data, code: res.code, message: res.message }
}

export const apiPostFormData = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    let endpoint = `${API_ENDPOINT}${url}`

    let formData = new FormData()

    const keys = Object.keys(body)
    for (let i = 0; i < keys.length; i++) {
        formData.append(keys[i], body[keys[i]])
    }

    let response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            Accept: 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
        },
        body: formData,
        credentials: 'include' // HttpOnly Cookie 전송을 위해 필요
    })

    // 401 에러 처리 (login, refresh는 제외)
    if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        if (isRefreshing) {
            await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
            
            // FormData 재생성
            formData = new FormData()
            for (let i = 0; i < keys.length; i++) {
                formData.append(keys[i], body[keys[i]])
            }
            
            response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/x-www-form-urlencoded',
                    Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
                },
                body: formData,
                credentials: 'include'
            })
        } else {
            isRefreshing = true
            
            try {
                const refreshResponse = await fetch(`${API_ENDPOINT}/auth/refresh`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                })

                if (!refreshResponse.ok) {
                    throw new Error('Refresh failed')
                }

                const refreshData = await refreshResponse.json()
                const newAccessToken = refreshData.data?.accessToken || refreshData.data?.token
                
                if (newAccessToken) {
                    setCookie(KEY_ACCESS_TOKEN, newAccessToken, { 
                        path: '/', 
                        maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                    })
                    
                    // Zustand State도 업데이트
                    const { useLoginStore } = await import('@/store/LoginStore.ts')
                    useLoginStore.getState().login('', newAccessToken)
                    
                    processQueue(null)
                    
                    // FormData 재생성
                    formData = new FormData()
                    for (let i = 0; i < keys.length; i++) {
                        formData.append(keys[i], body[keys[i]])
                    }
                    
                    response = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                            Accept: 'application/x-www-form-urlencoded',
                            Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
                        },
                        body: formData,
                        credentials: 'include'
                    })
                } else {
                    throw new Error('No access token in refresh response')
                }
            } catch (error) {
                processQueue(error)
                const { useLoginStore } = await import('@/store/LoginStore.ts')
                useLoginStore.getState().logout()
                window.location.href = '/login'
                throw error
            } finally {
                isRefreshing = false
            }
        }
    }

    const res = await response.json()
    return { data: res.data, code: res.code, message: res.message }
}

export const apiPatch = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    let endpoint = `${API_ENDPOINT}${url}`

    let response = await fetch(endpoint, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify(body),
        credentials: 'include' // HttpOnly Cookie 전송을 위해 필요
    })

    // 401 에러 처리 (login, refresh는 제외)
    if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        if (isRefreshing) {
            await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
            response = await fetch(endpoint, {
                method: 'PATCH',
                headers: getHeaders(),
                body: JSON.stringify(body),
                credentials: 'include'
            })
        } else {
            isRefreshing = true
            
            try {
                const refreshResponse = await fetch(`${API_ENDPOINT}/auth/refresh`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                })

                if (!refreshResponse.ok) {
                    throw new Error('Refresh failed')
                }

                const refreshData = await refreshResponse.json()
                const newAccessToken = refreshData.data?.accessToken || refreshData.data?.token
                
                if (newAccessToken) {
                    setCookie(KEY_ACCESS_TOKEN, newAccessToken, { 
                        path: '/', 
                        maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                    })
                    
                    // Zustand State도 업데이트
                    const { useLoginStore } = await import('@/store/LoginStore.ts')
                    useLoginStore.getState().login('', newAccessToken)
                    
                    processQueue(null)
                    
                    response = await fetch(endpoint, {
                        method: 'PATCH',
                        headers: getHeaders(),
                        body: JSON.stringify(body),
                        credentials: 'include'
                    })
                } else {
                    throw new Error('No access token in refresh response')
                }
            } catch (error) {
                processQueue(error)
                const { useLoginStore } = await import('@/store/LoginStore.ts')
                useLoginStore.getState().logout()
                window.location.href = '/login'
                throw error
            } finally {
                isRefreshing = false
            }
        }
    }

    const res = await response.json()
    return { data: res.data, code: res.code, message: res.message }
}

export const apiDelete = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    let endpoint = `${API_ENDPOINT}${url}`

    let response = await fetch(endpoint, {
        method: 'DELETE',
        headers: getHeaders(),
        body: JSON.stringify(body),
        credentials: 'include' // HttpOnly Cookie 전송을 위해 필요
    })

    // 401 에러 처리 (login, refresh는 제외)
    if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        if (isRefreshing) {
            await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
            response = await fetch(endpoint, {
                method: 'DELETE',
                headers: getHeaders(),
                body: JSON.stringify(body),
                credentials: 'include'
            })
        } else {
            isRefreshing = true
            
            try {
                const refreshResponse = await fetch(`${API_ENDPOINT}/auth/refresh`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                })

                if (!refreshResponse.ok) {
                    throw new Error('Refresh failed')
                }

                const refreshData = await refreshResponse.json()
                const newAccessToken = refreshData.data?.accessToken || refreshData.data?.token
                
                if (newAccessToken) {
                    setCookie(KEY_ACCESS_TOKEN, newAccessToken, { 
                        path: '/', 
                        maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                    })
                    
                    // Zustand State도 업데이트
                    const { useLoginStore } = await import('@/store/LoginStore.ts')
                    useLoginStore.getState().login('', newAccessToken)
                    
                    processQueue(null)
                    
                    response = await fetch(endpoint, {
                        method: 'DELETE',
                        headers: getHeaders(),
                        body: JSON.stringify(body),
                        credentials: 'include'
                    })
                } else {
                    throw new Error('No access token in refresh response')
                }
            } catch (error) {
                processQueue(error)
                const { useLoginStore } = await import('@/store/LoginStore.ts')
                useLoginStore.getState().logout()
                window.location.href = '/login'
                throw error
            } finally {
                isRefreshing = false
            }
        }
    }

    const res = await response.json()
    return { data: res.data, code: res.code, message: res.message }
}

export const apiPatchFormData = async <T>(url: string, body?: any): Promise<ApiRes<T>> => {
    let endpoint = `${API_ENDPOINT}${url}`

    let formData = new FormData()

    const keys = Object.keys(body)
    for (let i = 0; i < keys.length; i++) {
        formData.append(keys[i], body[keys[i]])
    }

    let response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
            Accept: 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
        },
        body: formData,
        credentials: 'include' // HttpOnly Cookie 전송을 위해 필요
    })

    // 401 에러 처리 (login, refresh는 제외)
    if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
        if (isRefreshing) {
            await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject })
            })
            
            // FormData 재생성
            formData = new FormData()
            for (let i = 0; i < keys.length; i++) {
                formData.append(keys[i], body[keys[i]])
            }
            
            response = await fetch(endpoint, {
                method: 'PATCH',
                headers: {
                    Accept: 'application/x-www-form-urlencoded',
                    Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
                },
                body: formData,
                credentials: 'include'
            })
        } else {
            isRefreshing = true
            
            try {
                const refreshResponse = await fetch(`${API_ENDPOINT}/auth/refresh`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                })

                if (!refreshResponse.ok) {
                    throw new Error('Refresh failed')
                }

                const refreshData = await refreshResponse.json()
                const newAccessToken = refreshData.data?.accessToken || refreshData.data?.token
                
                if (newAccessToken) {
                    setCookie(KEY_ACCESS_TOKEN, newAccessToken, { 
                        path: '/', 
                        maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                    })
                    
                    // Zustand State도 업데이트
                    const { useLoginStore } = await import('@/store/LoginStore.ts')
                    useLoginStore.getState().login('', newAccessToken)
                    
                    processQueue(null)
                    
                    // FormData 재생성
                    formData = new FormData()
                    for (let i = 0; i < keys.length; i++) {
                        formData.append(keys[i], body[keys[i]])
                    }
                    
                    response = await fetch(endpoint, {
                        method: 'PATCH',
                        headers: {
                            Accept: 'application/x-www-form-urlencoded',
                            Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
                        },
                        body: formData,
                        credentials: 'include'
                    })
                } else {
                    throw new Error('No access token in refresh response')
                }
            } catch (error) {
                processQueue(error)
                const { useLoginStore } = await import('@/store/LoginStore.ts')
                useLoginStore.getState().logout()
                window.location.href = '/login'
                throw error
            } finally {
                isRefreshing = false
            }
        }
    }

    const res = await response.json()
    return { data: res.data, code: res.code, message: res.message }
}


type Headers = {
    Accept: string
    'Content-Type': string
    Authorization?: string
}

const getHeaders = (): Headers => {
    return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getCookie(KEY_ACCESS_TOKEN)}`
    }
}
